// Browser support floor: latest 2 versions of evergreen browsers + Safari 15.4+.
// :focus-visible polyfill removed (Safari 15.4 ships native support, March 2022).

theme.fetchCache = {
  MAX_CACHE_SIZE: 20,
  cache: new Map(),
  pending: new Map(),

  /**
   * Fetches a URL and returns text, storing the result in a cache used for subsequent requests
   * to the same URL. Use theme.fetchCache.fetch(url) in theme code to return a Promise.
   * @param {Element} url - URL to fetch.
   * @returns {string} Text from request.
   */
  async fetchAndCache(url) {
    const response = await fetch(url);
    const text = await response.text();

    // Evict oldest if needed
    if (this.cache.size >= this.MAX_CACHE_SIZE) {
      const oldestKey = [...this.cache.entries()].sort((a, b) => a[1].timestamp - b[1].timestamp)[0][0];
      this.cache.delete(oldestKey);
    }

    this.cache.set(url, { text, timestamp: Date.now() });
    this.pending.delete(url);
    return text;
  },

  /**
   * Fetches a URL and returns text, using a local cache.
   * @param {Element} url - URL to fetch.
   * @returns {Promise} Promise that completes and returns the response as a string parameter.
   */
  fetch(url) {
    if (this.cache.has(url)) {
      return Promise.resolve(this.cache.get(url).text);
    }

    if (this.pending.has(url)) {
      return this.pending.get(url);
    }

    const promise = this.fetchAndCache(url);
    this.pending.set(url, promise);
    return promise;
  },

  /**
   * Preload a URL in the fetch cache.
   * @param {Element} url - URL to preload.
   */
  preload(url) {
    this.fetch(url).catch(() => {
      this.cache.delete(url);
      this.pending.delete(url);
    });
  },

  /**
   * Clear the cache.
   */
  clear() {
    this.cache.clear();
  },
};

/**
 * Creates a 'mediaMatches' object from the media queries specified in the theme,
 * and adds listeners for each media query. If a breakpoint is crossed, the mediaMatches
 * values are updated and a 'on:breakpoint-change' event is dispatched.
 */
(() => {
  const { mediaQueries } = theme;
  if (!mediaQueries) return;

  const mqKeys = Object.keys(mediaQueries);
  const mqLists = {};
  theme.mediaMatches = {};

  /**
   * Handles a media query (breakpoint) change.
   */
  const handleMqChange = () => {
    const newMatches = mqKeys.reduce((acc, media) => {
      acc[media] = !!(mqLists[media] && mqLists[media].matches);
      return acc;
    }, {});

    // Update mediaMatches values after breakpoint change.
    Object.keys(newMatches).forEach((key) => {
      theme.mediaMatches[key] = newMatches[key];
    });

    window.dispatchEvent(new CustomEvent('on:breakpoint-change'));
  };

  mqKeys.forEach((mq) => {
    // Create mqList object for each media query.
    mqLists[mq] = window.matchMedia(mediaQueries[mq]);

    // Get initial matches for each query.
    theme.mediaMatches[mq] = mqLists[mq].matches;

    // Add an event listener to each query.
    mqLists[mq].addEventListener('change', handleMqChange);
  });
})();

/**
 * Attaches OverlayScrollbars to one slider, loading both halves of the vendored
 * library on demand (`theme.utils.loadScript` / `loadStylesheet` cache per URL,
 * so every slider after the first shares the same fetch).
 *
 * Both halves, not just the script: OverlayScrollbars v2 reads layout from its
 * `.os-environment` probe DOM at construction time to detect the browser's
 * native scrollbar dimensions, and uses `[data-overlayscrollbars~='host']`
 * styles to position the host wrapper that replaces the slider's parent. Run
 * before its CSS applies, those reads return 0 / wrong values and every
 * downstream slider breaks — the host stops being a flex row, items wrap, and
 * the handles mis-size. If either half fails to load nothing is attached, and
 * `.slider { overflow-x: auto }` keeps native scrolling working.
 *
 * NOTE for callers that tear down: `instance.destroy()` does NOT remove the
 * inline layout styles the library writes on a viewport it did not create, so
 * anything that destroys must snapshot and restore the slider's `style`
 * attribute itself (see CarouselSlider.restoreSliderStyle).
 *
 * @param {HTMLElement} slider - the scrollport; its parent becomes the host.
 * @returns {Promise<object|null>} the instance, or null when none was attached.
 */
function attachOverlayScrollbars(slider) {
  const attach = () => {
    if (!window.OverlayScrollbarsGlobal) return null;
    return window.OverlayScrollbarsGlobal.OverlayScrollbars({ target: slider.parentElement, elements: { viewport: slider } }, {});
  };

  if (window.OverlayScrollbarsGlobal) return Promise.resolve(attach());

  if (!theme.utils || !theme.utils.loadScript || !theme.scripts || !theme.scripts.overlayScrollbars) {
    return Promise.resolve(null);
  }

  const cssReady = theme.utils.loadStylesheet && theme.styles && theme.styles.overlayScrollbars ? theme.utils.loadStylesheet(theme.styles.overlayScrollbars) : Promise.resolve();

  return Promise.all([cssReady, theme.utils.loadScript(theme.scripts.overlayScrollbars)])
    .then(attach)
    .catch(() => null);
}

// `debounce` is provided by assets/utils.js as `theme.utils.debounce`.
// Kept as a module-local alias so existing call sites keep working without rewrite.
const debounce = (fn, wait) => theme.utils.debounce(fn, wait);

/**
 * Sets a 'viewport-height' custom property on the root element.
 */
function setViewportHeight() {
  document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
}

/**
 * Sets a 'header-height' custom property on the root element.
 */
function setHeaderHeight() {
  const header = document.getElementById('shopify-section-header');
  if (!header) return;
  let height = header.offsetHeight;

  // Add announcement bar height (if shown).
  const announcement = document.getElementById('shopify-section-announcement');
  if (announcement) height += announcement.offsetHeight;

  document.documentElement.style.setProperty('--header-height', `${height}px`);
}

/**
 * Sets a 'scrollbar-width' custom property on the root element.
 */
function setScrollbarWidth() {
  document.documentElement.style.setProperty('--scrollbar-width', `${window.innerWidth - document.documentElement.clientWidth}px`);
}

/**
 * Sets the dimension variables.
 */
function setDimensionVariables() {
  setViewportHeight();
  setHeaderHeight();
  setScrollbarWidth();
}

// Set the dimension variables once the DOM is loaded
document.addEventListener('DOMContentLoaded', setDimensionVariables);

// Update the dimension variables if viewport resized.
window.addEventListener('resize', debounce(setDimensionVariables, 400));

// iOS alters screen width without resize event, if unexpectedly wide content is found
setTimeout(setViewportHeight, 3000);

/**
 * Adds an observer to initialise a script when an element is scrolled into view.
 * @param {Element} element - Element to observe.
 * @param {Function} callback - Function to call when element is scrolled into view.
 * @param {number} [threshold=500] - Distance from viewport (in pixels) to trigger init.
 */
function initLazyScript(element, callback, threshold = 500) {
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (typeof callback === 'function') {
              callback();
              observer.unobserve(entry.target);
            }
          }
        });
      },
      { rootMargin: `0px 0px ${threshold}px 0px` },
    );

    io.observe(element);
  } else {
    callback();
  }
}

/**
 * Pauses all media (videos/models) within an element.
 * @param {Element} [el=document] - Element to pause media within.
 * @param {Element} [excludeVideo] - Video element to not pause.
 */
function pauseAllMedia(el = document, excludeVideo = null) {
  el.querySelectorAll('.js-youtube, .js-vimeo, video').forEach((video) => {
    if (video === excludeVideo) return;
    const component = video.closest('video-component');
    if (component && component.dataset.background === 'true') return;

    if (video.matches('.js-youtube')) {
      video.contentWindow.postMessage('{ "event": "command", "func": "pauseVideo", "args": "" }', '*');
    } else if (video.matches('.js-vimeo')) {
      video.contentWindow.postMessage('{ "method": "pause" }', '*');
    } else {
      video.pause();
    }
  });

  el.querySelectorAll('product-model').forEach((model) => {
    try {
      if (model.modelViewerUI) model.modelViewerUI.pause();
    } catch {
      // Do nothing
    }
  });
}

class DeferredMedia extends theme.BaseElement {
  constructor() {
    super();

    const loadBtn = this.querySelector('.js-load-media');
    if (loadBtn) {
      loadBtn.addEventListener('click', this.loadContent.bind(this));
    } else {
      this.addObserver();
    }
  }

  /**
   * Adds an Intersection Observer to load the content when viewport scroll is near
   */
  addObserver() {
    if ('IntersectionObserver' in window === false) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.loadContent(false, false, 'observer');
            observer.unobserve(this);
          }
        });
      },
      { rootMargin: '0px 0px 1000px 0px' },
    );

    observer.observe(this);
  }

  /**
   * Loads the deferred media.
   * @param {boolean} [focus=true] - Focus the deferred media element after loading.
   * @param {boolean} [pause=true] - Whether to pause all media after loading.
   * @param {string} [loadTrigger='click'] - The action that caused the deferred content to load.
   */
  loadContent(focus = true, pause = true, loadTrigger = 'click') {
    if (pause) pauseAllMedia();
    if (this.getAttribute('loaded') !== null) return;

    this.loadTrigger = loadTrigger;
    const content = this.querySelector('template').content.firstElementChild.cloneNode(true);
    this.appendChild(content);
    this.setAttribute('loaded', '');

    const deferredEl = this.querySelector('video, model-viewer, iframe');
    if (deferredEl && focus) deferredEl.focus();
  }
}

customElements.define('deferred-media', DeferredMedia);

class DetailsDisclosure extends theme.BaseElement {
  constructor() {
    super();
    this.disclosure = this.querySelector('details');
    this.toggle = this.querySelector('summary');
    this.panel = this.toggle.nextElementSibling;
    this.transitionsEnabled = null;
    this.groupContainer = this.dataset.groupContainer ? this.closest(this.dataset.groupContainer) : null;

    this.on(this.toggle, 'click', this.handleToggle.bind(this));
    this.on(this.disclosure, 'transitionend', this.handleTransitionEnd.bind(this));
  }

  /**
   * Handles 'click' events on the summary element.
   * @param {object} evt - Event object.
   */
  handleToggle(evt) {
    if (this.transitionsEnabled === null) this.checkTransitionsEnabled();
    if (!this.transitionsEnabled) return;

    evt.preventDefault();

    if (!this.disclosure.open) {
      this.open();
    } else {
      this.close();
    }
  }

  /**
   * Check if transitions are enabled on this disclosure, saving this in this.transitionsEnabled
   */
  checkTransitionsEnabled() {
    // Style check may cause break in Chrome dev tools - do not perform on init
    this.transitionsEnabled = window.getComputedStyle(this.panel).transitionDuration !== '0s';
  }

  /**
   * Handles 'transitionend' events on the details element.
   * @param {object} evt - Event object.
   */
  handleTransitionEnd(evt) {
    if (evt.target !== this.panel) return;

    if (this.disclosure.classList.contains('is-closing')) {
      this.disclosure.classList.remove('is-closing');
      this.disclosure.open = false;
    }

    this.panel.removeAttribute('style');

    if (this.groupContainer) {
      this.groupContainer.style.minHeight = null;
    }
  }

  /**
   * Adds inline 'height' style to the content element, to trigger open transition.
   */
  addContentHeight() {
    this.panel.style.height = `${this.panel.scrollHeight}px`;
  }

  /**
   * Opens the details element.
   */
  open() {
    // If we're in a group container, close any open disclosures in the group
    if (this.groupContainer) {
      this.groupContainer.style.minHeight = `${this.groupContainer.getBoundingClientRect().height}px`;
      const openDisclosure = this.groupContainer.querySelector('details-disclosure:has(.disclosure[open])');
      if (openDisclosure) openDisclosure.close();
    }

    // Set content 'height' to zero before opening the details element.
    this.panel.style.height = '0';

    // Open the details element
    this.disclosure.open = true;

    // Set content 'height' to its scroll height, to enable CSS transition.
    this.addContentHeight();
  }

  /**
   * Closes the details element.
   */
  close() {
    // Set content height to its scroll height, to enable transition to zero.
    this.addContentHeight();

    // Add class to enable styling of content or toggle icon before or during close transition.
    this.disclosure.classList.add('is-closing');

    // Set content height to zero to trigger the transition.
    // Slight delay required to allow scroll height to be applied before changing to '0'.
    setTimeout(() => {
      this.panel.style.height = '0';
    });
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

// trapFocus / removeTrapFocus are provided by assets/utils.js as
// theme.utils.trapFocus / theme.utils.releaseFocus. These thin module-local
// shims preserve the legacy signature so existing call sites (cart-drawer.js,
// cart-items.js, details-modal.js, hotspots-image.js) keep working without rewrite.
let _activeTrapContainer = null;

/** @param {Element} container @param {Element} [elementToFocus=container] */
function trapFocus(container, elementToFocus = container) {
  if (_activeTrapContainer && _activeTrapContainer !== container) {
    theme.utils.releaseFocus(_activeTrapContainer);
  }
  _activeTrapContainer = container;
  theme.utils.trapFocus(container, elementToFocus);
}

/** @param {Element} [elementToFocus=null] - Element to focus after release. */
function removeTrapFocus(elementToFocus = null) {
  if (_activeTrapContainer) {
    theme.utils.releaseFocus(_activeTrapContainer);
    _activeTrapContainer = null;
  }
  if (elementToFocus) elementToFocus.focus();
}

class Modal extends theme.BaseElement {
  constructor() {
    super();
    this.addEventListener('click', this.handleClick.bind(this));
  }

  /**
   * Handles 'click' events on the modal.
   * @param {object} evt - Event object.
   */
  handleClick(evt) {
    if (evt.target !== this && !evt.target.matches('.js-close-modal')) return;
    this.close();
  }

  /**
   * Opens the modal.
   * @param {Element} opener - Modal opener element.
   */
  open(opener) {
    this.setAttribute('open', '');
    this.openedBy = opener;

    trapFocus(this);
    window.pauseAllMedia();

    // Add event handler (so the bound event listener can be removed).
    this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();

    // Add event listener (for while modal is open).
    this.addEventListener('keyup', this.keyupHandler);

    // Wrap tables in a '.scrollable-table' element for a better mobile experience.
    this.querySelectorAll(':not(.scrollable-table) > table').forEach((table) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'scrollable-table';
      table.parentNode.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    });
  }

  /**
   * Closes the modal.
   */
  close() {
    this.removeAttribute('open');

    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();

    // Remove event listener added on modal opening.
    this.removeEventListener('keyup', this.keyupHandler);
  }
}

customElements.define('modal-dialog', Modal);

class ModalOpener extends theme.BaseElement {
  constructor() {
    super();

    const button = this.querySelector('button');
    if (!button) return;

    button.addEventListener('click', () => {
      const modal = document.getElementById(this.dataset.modal);
      if (modal) modal.open(button);
    });
  }
}

customElements.define('modal-opener', ModalOpener);

class ProductCard extends theme.BaseElement {
  constructor() {
    super();
    window.initLazyScript(this, this.init.bind(this));
  }

  init() {
    this.images = this.querySelectorAll('.card__main-image');
    this.links = this.querySelectorAll('.js-prod-link');
    this.quickAddBtn = this.querySelector('.js-quick-add');
    this.carouselSlider = this.querySelector('product-card-image-slider');

    if (this.quickAddBtn) {
      this.productUrl = this.quickAddBtn.dataset.productUrl;
    } else if (this.links.length) {
      this.productUrl = this.links[0].href;
    }

    this.addEventListener('change', this.handleSwatchChange.bind(this));
  }

  /**
   * Handles 'change' events in the product card swatches.
   * @param {object} evt - Event object.
   */
  handleSwatchChange(evt) {
    if (!evt.target.matches('.opt-btn')) return;

    // Swap current card image to selected variant image.
    if (evt.target.dataset.mediaId && !this.carouselSlider) {
      const variantMedia = this.querySelector(`[data-media-id="${evt.target.dataset.mediaId}"]`);

      if (variantMedia) {
        this.images.forEach((image) => {
          image.hidden = true;
        });
        variantMedia.hidden = false;
      }
    }

    const url = new URL(this.productUrl, window.location.origin);
    url.searchParams.set('variant', evt.target.dataset.variantId);

    // Update link hrefs to url of selected variant.
    this.links.forEach((link) => {
      link.href = url.toString();
    });

    // Update the Quick Add button data.
    if (this.quickAddBtn) {
      this.quickAddBtn.dataset.selectedColor = evt.target.value;
    }
  }
}

customElements.define('product-card', ProductCard);

class QuantityInput extends theme.BaseElement {
  constructor() {
    super();
    this.input = this.querySelector('.qty-input__input');
    this.currentQty = this.input.value;
    this.changeEvent = new Event('change', { bubbles: true });

    this.addEventListener('click', this.handleClick.bind(this));
    this.input.addEventListener('focus', QuantityInput.handleFocus);
    this.input.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  /**
   * Handles 'click' events on the quantity input element.
   * @param {object} evt - Event object.
   */
  handleClick(evt) {
    if (!evt.target.matches('.qty-input__btn')) return;
    evt.preventDefault();

    this.currentQty = this.input.value;

    if (evt.target.name === 'plus') {
      this.input.stepUp();
    } else {
      this.input.stepDown();
    }

    if (this.input.value !== this.currentQty) {
      this.input.dispatchEvent(this.changeEvent);
      this.currentQty = this.input.value;
    }
  }

  /**
   * Handles 'focus' events on the quantity input element.
   * @param {object} evt - Event object.
   */
  static handleFocus(evt) {
    if (window.matchMedia('(pointer: fine)').matches) {
      evt.target.select();
    }
  }

  /**
   * Handles 'keydown' events on the input field.
   * @param {object} evt - Event object.
   */
  handleKeydown(evt) {
    if (evt.key !== 'Enter') return;
    evt.preventDefault();

    if (this.input.value !== this.currentQty) {
      this.input.blur();
      this.input.focus();
      this.currentQty = this.input.value;
    }
  }
}

customElements.define('quantity-input', QuantityInput);

class SideDrawer extends theme.BaseElement {
  constructor() {
    super();
    this.overlay = document.querySelector('.js-overlay');
  }

  /**
   * Handles a 'click' event on the drawer.
   * @param {object} evt - Event object.
   */
  handleClick(evt) {
    if (evt.target.matches('.js-close-drawer') || evt.target === this.overlay) {
      this.close();
    }
  }

  /**
   * Opens the drawer.
   * @param {Element} [opener] - Element that triggered opening of the drawer.
   * @param {Element} [elementToFocus] - Element to focus after drawer opened.
   * @param {Function} [callback] - Callback function to trigger after the open has completed
   */
  open(opener, elementToFocus, callback) {
    this.emit(`on:${this.dataset.name}:before-open`);

    this.overlay.classList.add('is-visible');
    this.setAttribute('open', '');
    this.setAttribute('aria-hidden', 'false');
    this.opener = opener;

    trapFocus(this, elementToFocus);

    // Create event handler variables (so the bound event listeners can be removed).
    this.clickHandler = this.clickHandler || this.handleClick.bind(this);
    this.keyupHandler = (evt) => {
      if (evt.key !== 'Escape' || evt.target.closest('.cart-drawer-popup')) return;
      this.close();
    };

    // Add event listeners (for while drawer is open).
    this.addEventListener('click', this.clickHandler);
    this.addEventListener('keyup', this.keyupHandler);
    this.overlay.addEventListener('click', this.clickHandler);

    // Handle events after the drawer opens
    const transitionDuration = parseFloat(getComputedStyle(this).getPropertyValue('--longest-transition-in-ms'));
    setTimeout(() => {
      if (callback) callback();
      this.emit(`on:${this.dataset.name}:after-open`);
    }, transitionDuration);
  }

  /**
   * Closes the drawer.
   * @param {Function} [callback] - Call back function to trigger after the close has completed
   */
  close(callback) {
    this.emit(`on:${this.dataset.name}:before-close`);

    this.removeAttribute('open');
    this.setAttribute('aria-hidden', 'true');
    this.overlay.classList.remove('is-visible');

    removeTrapFocus(this.opener);

    // Remove event listeners added on drawer opening.
    this.removeEventListener('click', this.clickHandler);
    this.removeEventListener('keyup', this.keyupHandler);
    this.overlay.removeEventListener('click', this.clickHandler);

    // Handle events after the drawer closes
    const transitionDuration = parseFloat(getComputedStyle(this).getPropertyValue('--longest-transition-in-ms'));
    setTimeout(() => {
      if (callback) callback();
      this.emit(`on:${this.dataset.name}:after-close`);
    }, transitionDuration);
  }
}

customElements.define('side-drawer', SideDrawer);

window.addEventListener(
  'resize',
  debounce(() => {
    window.dispatchEvent(new CustomEvent('on:debounced-resize'));
  }),
);

/**
 * Keeps a record of the height of the first occurrence of the given child element and stores it in
 * a css variable called `--[selector without the . or #]-height`. This height is kept up to date
 * when the browser changes size or the child element mutates.
 *
 * The selector must only match one element, and don't nest watched elements.
 *
 * Example usage:
 * <slide-show data-css-var-height=".quick-nav">
 * ... will result in:
 * <slide-show data-css-var-height=".quick-nav" style="--quick-nav-height: 483px;">
 */
function initCssVarHeightWatch() {
  const parentElems = document.querySelectorAll('[data-css-var-height]');
  if (!parentElems.length) return;

  const updateHeight = (elem) => {
    const parentElem = elem.closest('[data-css-var-height]');
    if (parentElem) {
      const selectors = parentElem.dataset.cssVarHeight.split(',');
      let matchedSelector = null;
      selectors.forEach((selector) => {
        if (elem.matches(selector.trim())) {
          matchedSelector = selector.trim();
        }
      });

      const variableName = `--${matchedSelector.replace(/^([#.])/, '')}-height`;
      parentElem.style.setProperty(variableName, `${elem.getBoundingClientRect().height.toFixed(2)}px`);
    }
  };

  // Single shared MutationObserver across all invocations (DOMContentLoaded +
  // every shopify:section:load), so observers don't pile up. observe() is
  // additive and re-observing the same node is a no-op.
  if (!initCssVarHeightWatch.observer && 'MutationObserver' in window) {
    initCssVarHeightWatch.observer = new MutationObserver(
      debounce((mutationList) => {
        const elemToWatch = mutationList[0].target.closest('[data-css-var-height]');
        if (elemToWatch) {
          updateHeight(elemToWatch.querySelector(elemToWatch.dataset.cssVarHeight));
        }
      }),
    );
  }
  const mutationObserver = initCssVarHeightWatch.observer;

  parentElems.forEach((parentElem) => {
    parentElem.dataset.cssVarHeight.split(',').forEach((selector) => {
      const elemToWatch = parentElem.querySelector(selector);
      if (elemToWatch) {
        if (mutationObserver) {
          mutationObserver.observe(elemToWatch, {
            childList: true,
            attributes: true,
            subtree: true,
          });
        }

        updateHeight(elemToWatch);
      }
    });
  });

  // Register the resize / update listeners ONCE across all invocations, instead
  // of per-element on every section load (which leaked a growing stack of
  // listeners on window/document — ADR-0001). The handler re-queries the DOM,
  // so elements added by later section loads are covered too.
  if (!initCssVarHeightWatch.listenersBound) {
    initCssVarHeightWatch.listenersBound = true;
    const updateAll = () => {
      document.querySelectorAll('[data-css-var-height]').forEach((parentElem) => {
        parentElem.dataset.cssVarHeight.split(',').forEach((selector) => {
          const elem = parentElem.querySelector(selector.trim());
          if (elem) updateHeight(elem);
        });
      });
    };
    window.addEventListener('on:debounced-resize', updateAll);
    document.addEventListener('on:css-var-height:update', updateAll);
  }
}

document.addEventListener('DOMContentLoaded', initCssVarHeightWatch);
document.addEventListener('shopify:section:load', initCssVarHeightWatch);

/**
 * Provides convenient utility functions for interacting with elements
 */
(() => {
  theme.elementUtil = {};

  /**
   * Allows for removal of elements in one line of code
   * @param {object} elem - Element to remove
   */
  theme.elementUtil.remove = (elem) => {
    if (elem) {
      if (typeof elem.remove === 'function') {
        elem.remove();
      } else {
        elem.forEach((thisElem) => {
          thisElem.remove();
        });
      }
    }
  };

  /**
   * Checks if the passed element is in viewport or not
   * @param {object} elem - Element to check the view of
   * @returns {boolean}
   */
  theme.elementUtil.isInViewport = (elem) => {
    const rect = elem.getBoundingClientRect();
    return (
      Math.round(rect.top) >= 0 &&
      Math.round(rect.left) >= 0 &&
      Math.round(rect.bottom) <= (window.innerHeight || document.documentElement.clientHeight) &&
      Math.round(rect.right) <= (window.innerWidth || document.documentElement.clientWidth)
    );
  };
})();

/**
 * Utility functions for interacting with LocalStorage/SessionStorage
 */
(() => {
  theme.storageUtil = {};

  theme.storageUtil.set = (key, value, isSession) => {
    if (isSession) {
      sessionStorage.setItem(`rfc-${key}`, typeof value === 'object' ? JSON.stringify(value) : value);
    } else {
      localStorage.setItem(`rfc-${key}`, typeof value === 'object' ? JSON.stringify(value) : value);
    }
  };

  theme.storageUtil.get = (key, isJson, isSession) => {
    let value = isSession ? sessionStorage.getItem(`rfc-${key}`) : localStorage.getItem(`rfc-${key}`);
    if (isJson) {
      value = JSON.parse(value);
    }
    return value;
  };

  theme.storageUtil.remove = (key, isSession) => {
    if (isSession) {
      sessionStorage.removeItem(`rfc-${key}`);
    } else {
      localStorage.removeItem(`rfc-${key}`);
    }
  };
})();

class StoreHeader extends theme.BaseElement {
  constructor() {
    super();
    this.menu = this.querySelector('.main-menu__content');
    this.searchToggle = this.querySelector('.js-show-search');
    this.searchToggleLeft = this.querySelector('.js-show-search-left');
    this.mobNavToggle = this.querySelector('.main-menu__toggle');
    this.cartIcon = this.querySelector('#cart-icon');
    this.headerGroupSections = document.querySelectorAll('.shopify-section-group-header-group');

    this.stickyInitialised = false;
    this.stickyTransitioning = false;
    this.lastScrollPos = 0;

    this.headerTransitionSpeed = parseFloat(getComputedStyle(this).getPropertyValue('--header-transition-speed'));

    window.setHeaderHeight();
    this.bindEvents();
    this.init();
    this.initCartShake();

    document.addEventListener('DOMContentLoaded', this.setMenuHeight.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('on:debounced-resize', this.resizeHandler);
    if (this.breakpointChangeHandler) {
      window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
    }
    clearTimeout(this.shakeCleanupTimer);
  }

  bindEvents() {
    this.resizeHandler = this.resizeHandler || this.updateHeaderHeights.bind(this);
    window.addEventListener('on:debounced-resize', this.resizeHandler);

    this.mobNavToggle.addEventListener('click', window.setHeaderHeight);
    this.mobNavToggle.addEventListener('click', this.setHeaderEnd.bind(this));

    if (this.dataset.isSticky) {
      this.breakpointChangeHandler = this.breakpointChangeHandler || this.init.bind(this);
      window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);
    }

    if (this.dataset.isSearchMinimised) {
      if (this.searchToggle) {
        this.searchToggle.addEventListener('click', this.handleSearchToggleClick.bind(this));
      }

      if (this.searchToggleLeft) {
        this.searchToggleLeft.addEventListener('click', this.handleSearchToggleClick.bind(this));
      }
    }
  }

  /**
   * Init sticky behaviour of the header
   */
  init() {
    this.updateHeaderHeights();

    if (this.dataset.isSticky) {
      if (theme.mediaMatches.md && !this.stickyInitialised) {
        this.stickyInitialised = true;
        // Animate the menu in/out on scroll up/down
        window.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });
      }

      setTimeout(() => {
        document.querySelector('.rfc-header').classList.add('rfc-header--sticky');
      });

      this.setMenuHeight();
      this.setHeaderEnd();
    }
  }

  /**
   * Cart shake: draws attention to the cart icon when an item is added to the
   * cart. Opt-in via the `cart_shaking` theme setting, exposed to JS as
   * `theme.settings.cartShaking` (snippets/theme-globals.liquid).
   *
   * Listens to the DOM cart events (product-form.js / cart-items.js) instead of
   * theme.pubsub: the pubsub bus only carries a generic `cartUpdate` with no
   * add/remove or quantity-delta info (see pubsub.js EVENTS), so it cannot tell
   * an add from a removal. These DOM events can, letting us shake only when
   * items are ADDED. Listening on `document` is also independent of script load
   * order. The visual is the `.header__icon--cart-shake` animation in main.css.
   */
  initCartShake() {
    if (!theme.settings || !theme.settings.cartShaking || !this.cartIcon) return;

    // A brand-new line item was added (regular add-to-cart or quick-add drawer).
    this.on(document, 'on:cart:add', () => this.shakeCart());

    // An existing line's quantity changed. Shake only on an increase (adding
    // more of something already in the cart), never on a decrement or removal.
    this.on(document, 'on:line-item:change', (evt) => {
      if (Number(evt.detail?.newQuantity) > Number(evt.detail?.oldQuantity)) this.shakeCart();
    });
  }

  /**
   * (Re)starts the cart-icon shake animation. Remove -> reflow -> re-add
   * restarts the CSS animation even mid-shake (a rapid second add). The class
   * is cleared once the sequence has finished (icon 0.9s x2, plus the count
   * bubble's `calc(--shake-delay + 1.2s)` + 1s tail, is about 2.2s) so it does
   * not linger and re-fire the count animation when the bubble re-renders on a
   * later cart update.
   */
  shakeCart() {
    clearTimeout(this.shakeCleanupTimer);
    this.cartIcon.classList.remove('header__icon--cart-shake');
    void this.cartIcon.offsetWidth; // force reflow so the animation restarts
    this.cartIcon.classList.add('header__icon--cart-shake');
    this.shakeCleanupTimer = setTimeout(() => {
      this.cartIcon.classList.remove('header__icon--cart-shake');
    }, 2400);
  }

  /**
   * Toggles visibility of the search bar
   * @param {object} evt - Event object
   */
  handleSearchToggleClick(evt) {
    evt.preventDefault();
    const searchBar = this.querySelector('.js-search-bar');
    if (this.classList.contains('search-is-collapsed')) {
      this.classList.remove('search-is-collapsed');

      // Wait for reveal animation to complete
      setTimeout(() => {
        this.classList.add('search-is-visible');
        const searchInput = searchBar.querySelector('.js-search-input');
        searchInput.focus();
        window.setHeaderHeight();
      }, this.headerTransitionSpeed);
    } else {
      this.classList.remove('search-is-visible');

      setTimeout(() => {
        this.classList.add('search-is-collapsed');
      });

      setTimeout(window.setHeaderHeight, this.headerTransitionSpeed);
    }
  }

  /**
   * Wrapper for calling the two below
   */
  updateHeaderHeights() {
    if (theme.mediaMatches.md) {
      this.setMenuHeight();
      setTimeout(window.setHeaderHeight, this.headerTransitionSpeed);
    } else {
      window.setHeaderHeight();
    }

    // Set a css variable to record where the page content starts
    if (this.headerGroupSections && this.headerGroupSections.length > 0) {
      let headerGroupHeight = 0;
      this.headerGroupSections.forEach((section) => {
        headerGroupHeight += section.getBoundingClientRect().height;
      });

      if (headerGroupHeight > 0) {
        document.documentElement.style.setProperty('--content-start', `${headerGroupHeight.toFixed(1)}px`);
      }
    }
  }

  /**
   * Set a css variable to the height of the menu links
   */
  setMenuHeight() {
    if (this.menu && this.menu.clientHeight) {
      this.style.setProperty('--menu-height', `${this.menu.clientHeight + 16}px`);
      document.documentElement.style.setProperty('--header-height', `${this.clientHeight}px`);
    }
  }

  /**
   * Handles 'scroll' event on the header
   */
  handleScroll() {
    if (!document.body.classList.contains('fixed') && !this.stickyTransitioning) {
      if (document.documentElement.scrollTop < 200) {
        this.show();
      } else if (this.lastScrollPos < document.documentElement.scrollTop) {
        this.hide();
      } else if (this.lastScrollPos - 5 > document.documentElement.scrollTop) {
        this.show();
      }

      this.lastScrollPos = document.documentElement.scrollTop;
    }
  }

  /**
   * Set a css variable to indicate where the nav ends on the page
   */
  setHeaderEnd() {
    const headerEnd = Number(this.getBoundingClientRect().top + this.clientHeight);
    document.documentElement.style.setProperty('--header-end', `${headerEnd.toFixed(1)}px`);
    document.documentElement.style.setProperty('--header-end-padded', `${(headerEnd + (theme.mediaMatches.md ? 56 : 20)).toFixed(1)}px`);
  }

  /**
   * Updates hidden class for header element
   */
  show() {
    this.classList.remove('is-out');
    this.stickyTransitioning = true;
    setTimeout(() => {
      this.lastScrollPos = document.documentElement.scrollTop;
      this.stickyTransitioning = false;
      this.handleScroll();
      this.setHeaderEnd();
    }, 300);
  }

  /**
   * Updates hidden class for header element
   */
  hide() {
    if (!this.stickyTransitioning) {
      this.classList.add('is-out');
      this.stickyTransitioning = true;
      setTimeout(() => {
        this.lastScrollPos = document.documentElement.scrollTop;
        this.stickyTransitioning = false;
        this.handleScroll();
        this.setHeaderEnd();
      }, 300);
    }
  }
}

customElements.define('store-header', StoreHeader);

class MainMenu extends theme.BaseElement {
  constructor() {
    super();
    this.mainContent = this.querySelector('.main-menu__content');
    this.mainToggle = this.querySelector('.main-menu__toggle');
    this.firstLevelMenuLinks = this.querySelectorAll('.js-nav-hover');
    this.firstLevelSingleLinks = this.querySelectorAll('.main-nav__row--leaf');
    this.nav = this.querySelector('.main-nav');
    this.overlay = document.querySelector('.js-overlay');
    this.searchIcon = document.querySelector('.header__icons .js-show-search');
    this.sidebarLinks = this.querySelectorAll('.js-sidebar-hover');
    this.elementsWhichCloseMenus = document.querySelectorAll('.js-closes-menu');

    this.isTouchEvent = false;
    this.childNavOpen = false;
    this.overlayOpen = false;

    // Deterministic close finalisation. Maps a disclosure element to the
    // setTimeout id that will strip its `.is-open` class once the fade-out
    // duration has elapsed. Replaces a transitionend-based state machine
    // that silently dropped state when transitions were cancelled mid-flight
    // (e.g. a re-open swap), leaving menus stuck "open without visible".
    this.closeTimers = new Map();
    // Read once — the token doesn't change at runtime, and getComputedStyle on
    // every close adds up across rapid hover swaps.
    this.fadeMs = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ease-duration-fast')) || 150;

    this.addListeners();
    this.init();
    // Hide right-side quicklinks when the main nav would otherwise wrap to a
    // second row. Width-based media queries can't see how many top-level menu
    // items a merchant configured nor how long their labels are, so a JS
    // measurement is the only reliable trigger.
    this.setupQuicklinksOverflowGuard();
    this.setupMenuMedia();
  }

  /**
   * Fills the mega-menu thumbnails and collection labels, which the header
   * leaves out of the page render (ADR-0022): each one costs the server a read
   * per link, a products query for every collection without its own image,
   * and a large menu made that most of every page's render time.
   *
   * The header names its section in `data-menu-media-section` and renders one
   * empty `[data-menu-media]` box per thumbnail, keyed by the link's URL and
   * the box's role. The same section fetched through the Section Rendering API
   * renders the media, and each box's contents are copied across by key.
   *
   * Once per browser session: the boxes' contents are kept in sessionStorage
   * under the section, theme version and locale root, so later pages fill the
   * menu with no request. A pointer device loads them when the browser is idle
   * after load, so a menu opened later is already complete; a touch device
   * waits for the first menu interaction, since many visitors never open it.
   */
  setupMenuMedia() {
    this.menuMediaSection = this.dataset.menuMediaSection;
    if (!this.menuMediaSection || !this.querySelector('[data-menu-media]')) return;

    const load = () => this.loadMenuMedia();
    this.on(this.nav, 'pointerenter', load);
    this.on(this.nav, 'focusin', load);
    this.on(this.nav, 'touchstart', load, { passive: true });
    this.on(this.mainToggle, 'click', load);

    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      const whenIdle = () => (window.requestIdleCallback ? window.requestIdleCallback(load, { timeout: 4000 }) : setTimeout(load, 2000));
      if (document.readyState === 'complete') whenIdle();
      else this.on(window, 'load', whenIdle, { once: true });
    }
  }

  /**
   * Loads and applies the menu media, once. A failure is logged and forgotten,
   * so the next interaction retries; the menu works without its thumbnails.
   * @returns {Promise<void>}
   */
  loadMenuMedia() {
    if (!this.menuMediaPromise) {
      this.menuMediaPromise = this.fetchMenuMedia()
        .then((media) => this.applyMenuMedia(media))
        .catch((error) => {
          this.menuMediaPromise = null;
          console.warn('Menu images could not be loaded:', error);
        });
    }
    return this.menuMediaPromise;
  }

  /**
   * The menu media as a `{ key: innerHTML }` map, from this session's cache or
   * the Section Rendering API. A cached map missing any of this page's keys
   * (the menu changed during the session) is fetched again.
   * @returns {Promise<Record<string, string>>}
   */
  async fetchMenuMedia() {
    const root = window.Shopify?.routes?.root || '/';
    const cacheKey = `rfc-menu-media:${this.menuMediaSection}:${theme.info?.version || ''}:${root}`;
    const keys = [...this.querySelectorAll('[data-menu-media]')].map((el) => el.dataset.menuMedia);

    try {
      const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
      if (cached && keys.every((key) => key in cached)) return cached;
    } catch {
      // Storage blocked or the entry unreadable: fetch instead.
    }

    const response = await fetch(`${root}?section_id=${encodeURIComponent(this.menuMediaSection)}`);
    if (!response.ok) throw new Error(`Section Rendering API returned ${response.status}`);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const media = {};
    doc.querySelectorAll('[data-menu-media]').forEach((el) => {
      media[el.dataset.menuMedia] = el.innerHTML;
    });

    try {
      sessionStorage.setItem(cacheKey, JSON.stringify(media));
    } catch {
      // Quota or privacy mode: this page is filled regardless.
    }
    return media;
  }

  /**
   * Copies each box's media in by key, then promotes the images of any panel
   * already open — `open()` ran before they existed.
   * @param {Record<string, string>} media
   */
  applyMenuMedia(media) {
    this.querySelectorAll('[data-menu-media]').forEach((el) => {
      const html = media[el.dataset.menuMedia];
      if (typeof html === 'string') el.innerHTML = html;
    });
    this.querySelectorAll('.main-nav__item--dropdown.is-open').forEach((disclosure) => this.hydrateImages(disclosure));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('focusin', this.focusOutHandler);
    window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);

    // Cancel any pending close finalisers. Shopify reloads sections (theme
    // editor, section:load events) by detaching and re-attaching the element;
    // without cleanup the old instance's callback would run against a stale or
    // partial DOM.
    this.closeTimers.forEach((tid) => clearTimeout(tid));
    this.closeTimers.clear();

    if (this.quicklinksOverflowObserver) {
      this.quicklinksOverflowObserver.disconnect();
      this.quicklinksOverflowObserver = null;
    }

    if (Shopify.designMode) {
      document.removeEventListener('shopify:block:select', this.blockSelectHandler);
      document.removeEventListener('shopify:block:deselect', this.blockDeselectHandler);
    }
  }

  addListeners() {
    this.focusOutHandler = this.focusOutHandler || this.handleFocusOut.bind(this);
    this.breakpointChangeHandler = this.breakpointChangeHandler || this.init.bind(this);

    this.mainContent.addEventListener('transitionend', this.handleMainContentTransition.bind(this));
    this.mainToggle.addEventListener('click', this.handleMainMenuToggle.bind(this));
    this.on(
      this.nav,
      'touchstart',
      () => {
        this.isTouchEvent = true;
      },
      { passive: true },
    );
    this.nav.addEventListener('click', this.handleNavClick.bind(this));
    this.on(
      this.nav,
      'touchend',
      () =>
        setTimeout(() => {
          this.isTouchEvent = false;
        }, 100),
      { passive: true },
    );
    window.addEventListener('focusin', this.focusOutHandler);
    window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);

    if (Shopify.designMode) {
      this.blockSelectHandler = this.blockSelectHandler || this.handleBlockSelect.bind(this);
      this.blockDeselectHandler = this.blockDeselectHandler || this.handleBlockDeselect.bind(this);
      document.addEventListener('shopify:block:select', this.blockSelectHandler);
      document.addEventListener('shopify:block:deselect', this.blockDeselectHandler);
    }

    if (!theme.mediaMatches.md && this.searchIcon) {
      this.searchIcon.addEventListener('click', this.closeMainContent.bind(this));
    }
  }

  /**
   * Sets initial state of the main content element.
   * @param {?object} evt - Event object.
   */
  init(evt) {
    if (!evt) {
      // On mobile, ensure main content is hidden; on desktop, CSS handles visibility.
      if (!theme.mediaMatches.md) {
        this.mainContent.classList.remove('is-open', 'is-visible');
        this.mainToggle.setAttribute('aria-expanded', 'false');
      }
    } else {
      // Any breakpoint change — reset all menu state.
      this.closeMainMenu(false);
      this.childNavOpen = false;

      this.closeOpenDisclosures({ transition: false });

      if (this.overlayOpen) this.toggleOverlay(false);
    }

    if (theme.device.hasHover) {
      // Add event handler (so the bound event listener can be removed)
      this.mouseEnterMenuLinkHandler = this.mouseEnterMenuLinkHandler || this.handleMouseEnterMenuLink.bind(this);
      this.mouseLeaveMenuLinkHandler = this.mouseLeaveMenuLinkHandler || this.handleMouseLeaveMenuLink.bind(this);
      this.mouseEnterSingleLinkHandler = this.mouseEnterSingleLinkHandler || this.handleMouseEnterSingleLink.bind(this);
      this.mouseLeaveSingleLinkHandler = this.mouseLeaveSingleLinkHandler || this.handleMouseLeaveSingleLink.bind(this);
      this.mouseEnterMenuCloserHandler = this.mouseEnterMenuCloserHandler || this.handleClose.bind(this);
      this.mouseEnterNavDropdownHandler =
        this.mouseEnterNavDropdownHandler ||
        ((e) => {
          // Defensive: a missed mouseleave during fast swaps would race two timers.
          if (this.menuLinkTimeout) clearTimeout(this.menuLinkTimeout);
          // Hydrate deferred images on hover intent — the menuSensitivity delay
          // (typically 200ms) doubles as fetch head-start, so images are warming
          // up by the time the panel actually fades in.
          const disclosure = e.target.closest('.main-nav__item--dropdown');
          if (disclosure) this.hydrateImages(disclosure);
          this.menuLinkTimeout = setTimeout(this.openMenuFromMouseEnter.bind(this, e.target), Number.parseInt(this.dataset.menuSensitivity, 10));
        });

      // Desktop-menu-trigger=click suppresses hover-to-open across all
      // first-level dropdowns. Chevron `.js-toggle` and primary-link
      // redirect (handleNavClick) still drive open/close, and open()
      // itself hydrates deferred images at click time (line 1572) — so
      // dropping the hover-side prefetch in line 1163 doesn't break
      // image loading, only loses the ~200ms head-start.
      const hoverOpenEnabled = this.dataset.desktopMenuTrigger !== 'click';

      // Bind listening events for mouse enter/leave a main menu link
      if (!this.mouseOverListening && theme.mediaMatches.md && hoverOpenEnabled) {
        this.firstLevelMenuLinks.forEach((menuLink) => {
          const handler = menuLink.closest('.main-nav__item--dropdown') ? this.mouseEnterNavDropdownHandler : this.mouseEnterMenuLinkHandler;
          menuLink.addEventListener('mouseenter', handler);
          menuLink.addEventListener('mouseleave', this.mouseLeaveMenuLinkHandler);
        });
        this.firstLevelSingleLinks.forEach((singleLink) => {
          singleLink.addEventListener('mouseenter', this.mouseEnterSingleLinkHandler);
          singleLink.addEventListener('mouseleave', this.mouseLeaveSingleLinkHandler);
        });
        this.elementsWhichCloseMenus.forEach((elem) => {
          elem.addEventListener('mouseenter', this.mouseEnterMenuCloserHandler);
        });
        this.mouseOverListening = true;
      } else if (this.mouseOverListening && (!theme.mediaMatches.md || !hoverOpenEnabled)) {
        this.firstLevelMenuLinks.forEach((menuLink) => {
          const handler = menuLink.closest('.main-nav__item--dropdown') ? this.mouseEnterNavDropdownHandler : this.mouseEnterMenuLinkHandler;
          menuLink.removeEventListener('mouseenter', handler);
          menuLink.removeEventListener('mouseleave', this.mouseLeaveMenuLinkHandler);
        });
        this.firstLevelSingleLinks.forEach((singleLink) => {
          singleLink.removeEventListener('mouseenter', this.mouseEnterSingleLinkHandler);
          singleLink.removeEventListener('mouseleave', this.mouseLeaveSingleLinkHandler);
        });
        this.elementsWhichCloseMenus.forEach((elem) => {
          elem.removeEventListener('mouseenter', this.mouseEnterMenuCloserHandler);
        });
        this.mouseOverListening = false;
      }

      if (this.sidebarLinks) {
        if (!this.mouseOverSidebarListening && theme.mediaMatches.md) {
          this.sidebarLinks.forEach((sidebarLink) => {
            sidebarLink.addEventListener('mouseenter', MainMenu.handleSidenavMenuToggle);
            sidebarLink.addEventListener('focusin', MainMenu.handleSidenavFocusIn);
          });
          this.mouseOverSidebarListening = true;
        } else if (this.mouseOverSidebarListening && !theme.mediaMatches.md) {
          this.sidebarLinks.forEach((sidebarLink) => {
            sidebarLink.removeEventListener('mouseenter', MainMenu.handleSidenavMenuToggle);
            sidebarLink.removeEventListener('focusin', MainMenu.handleSidenavFocusIn);
          });
          this.mouseOverSidebarListening = false;
        }
      }
    }
  }

  /**
   * Close the menu if the nav loses focus
   * @param {object} evt - Event object.
   */
  handleFocusOut(evt) {
    if (!this.contains(evt.target) && this.overlayOpen) this.handleClose();
  }

  /**
   * Handles 'focusin' event on sidebar links — triggers the sidenav toggle.
   * @param {object} evt - Event object.
   */
  static handleSidenavFocusIn(evt) {
    if (evt.currentTarget.querySelector('.child-nav__panel')) {
      MainMenu.handleSidenavMenuToggle(null, evt.currentTarget);
    }
  }

  /**
   * Updates the visible sidebar item.
   * @param {object|null} evt - Event object, or null when called programmatically.
   * @param {Element} [listElem=evt.target] - The sidebar list element to make visible.
   */
  static handleSidenavMenuToggle(evt, listElem = evt.target) {
    const container = listElem.closest('.child-nav');
    const lastSidenavElem = container.querySelector('.is-visible');
    if (lastSidenavElem) {
      lastSidenavElem.classList.remove('is-visible');
    }
    listElem.classList.add('is-visible');

    // Maintain a CSS variable which records the height of the current sidebar panel
    const openPanel = listElem.querySelector('.child-nav__panel');
    if (openPanel) {
      container.style.setProperty('--sidebar-height', `${Number.parseInt(openPanel.getBoundingClientRect().height, 10)}px`);
    }
  }

  /**
   * Handles 'click' event on the main menu toggle button.
   * @param {object} evt - Event object.
   */
  handleMainMenuToggle(evt) {
    evt.preventDefault();
    this.opener = this.mainToggle;

    if (!this.mainContent.classList.contains('is-open')) {
      this.openMainMenu();
    } else {
      this.closeMainMenu(true);
    }
  }

  /**
   * Opens the main menu content with a two-step animation.
   */
  openMainMenu() {
    this.mainContent.classList.add('is-open');

    if (this.nav.querySelector('.main-nav__item--dropdown.is-open')) {
      this.mainContent.classList.add('main-menu__content--no-focus');
    }

    // Double rAF ensures display: block has been painted before the transition fires
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.mainContent.classList.add('is-visible');
      });
    });

    this.mainToggle.setAttribute('aria-expanded', 'true');

    removeTrapFocus();
    trapFocus(this);

    document.body.classList.add('overflow-hidden');

    if (!document.querySelector('store-header[data-is-sticky="true"]')) {
      document.body.classList.add('fixed');
    }
  }

  /**
   * Closes the main menu content.
   * @param {boolean} [transition=true] - Whether to run the exit transition.
   */
  closeMainMenu(transition = true) {
    this.mainContent.classList.remove('is-visible', 'main-menu__content--no-focus');
    this.mainToggle.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('overflow-hidden', 'fixed');

    if (!transition) {
      this.mainContent.classList.remove('is-open');
      removeTrapFocus(this.opener);
      this.opener = null;
    }
    // When transition=true, handleMainContentTransition removes is-open after the transition ends.
  }

  /**
   * Handles 'transitionend' on the main content — removes is-open once the exit transition ends.
   * @param {object} evt - Event object.
   */
  handleMainContentTransition(evt) {
    if (evt.propertyName !== 'opacity') return;

    if (!this.mainContent.classList.contains('is-visible')) {
      this.mainContent.classList.remove('is-open');
      removeTrapFocus(this.opener);
      this.opener = null;
    }
  }

  /**
   * Handles 'mouseenter' event on the main menu items using a timeout to infer hover intent
   * @param {object} evt - Event object
   */
  handleMouseEnterMenuLink(evt) {
    if (this.menuLinkTimeout) clearTimeout(this.menuLinkTimeout);
    this.menuLinkTimeout = setTimeout(this.openMenuFromMouseEnter.bind(this, evt.target), Number.parseInt(this.dataset.menuSensitivity, 10));
  }

  /**
   * Handles 'mouseleave' event on the main menu items - clears the timeout
   */
  handleMouseLeaveMenuLink() {
    if (this.menuLinkTimeout) clearTimeout(this.menuLinkTimeout);
  }

  /**
   * Handles 'mouseenter' event on links with no submenu items using a timeout to infer hover intent
   */
  handleMouseEnterSingleLink() {
    if (this.singleLinkTimeout) clearTimeout(this.singleLinkTimeout);
    this.singleLinkTimeout = setTimeout(
      () => {
        this.handleClose();
      },
      Number.parseInt(this.dataset.menuSensitivity, 10),
    );
  }

  /**
   * Handles 'mouseleave' event on links with no submenu - clears the timeout
   */
  handleMouseLeaveSingleLink() {
    if (this.singleLinkTimeout) clearTimeout(this.singleLinkTimeout);
  }

  /**
   * Opens the menu being hovered over
   * @param {Element} menuElem - The menu element to open.
   */
  openMenuFromMouseEnter(menuElem) {
    trapFocus(menuElem);

    const disclosure = menuElem.closest('.main-nav__item--dropdown');

    // Already fully open — nothing to do.
    if (disclosure.classList.contains('is-visible')) return;

    // If this disclosure is mid-close (.is-open present, .is-visible stripped),
    // the overlay is already on for it — only toggle the overlay when both
    // (a) no other menu was open to inherit it and (b) we aren't reversing
    // a close on this same disclosure.
    const wasMidClose = disclosure.classList.contains('is-open');
    if (this.closeOpenDisclosures({ except: disclosure }) === 0 && !wasMidClose) {
      this.toggleOverlay(!this.overlayOpen);
    }

    // open() handles both fresh open and mid-close reverse: it cancels the
    // pending close timer, leaves .is-open in place (idempotent classList.add),
    // and the rAF chain re-applies .is-visible — the opacity transition
    // reverses naturally from whatever value the fade-out had reached.
    this.open(disclosure);
  }

  /**
   * Handles 'click' event on the nav.
   * @param {object} evt - Event object.
   */
  handleNavClick(evt) {
    const mainMenuContent = evt.target.closest('.main-menu__content');
    const navLink = evt.target.closest('.main-nav__item--primary, .child-nav__item');
    const hasHiddenToggle =
      theme.mediaMatches.md && !!evt.target.closest('.mega-nav--columns .child-nav__item--toggle, .mega-nav--pills .child-nav__item--toggle, .mega-nav--sidebar .child-nav__item--toggle');
    let el = evt.target;

    // If a # link has a sibling toggle, redirect the click to that toggle
    if (navLink?.href?.endsWith('#')) {
      evt.preventDefault();
      const siblingToggle = navLink.parentElement?.querySelector('.js-toggle');
      if (siblingToggle && !hasHiddenToggle) {
        el = siblingToggle;
      }

      // Treat clicks on nav links with a sibling toggle as toggle clicks
      // rather than navigating. Triggered by data-link-toggle on its
      // configured breakpoints, or by desktop click-trigger mode.
    } else if (
      this.dataset.linkToggle === 'mobile_desktop' ||
      (this.dataset.linkToggle === 'mobile' && !theme.mediaMatches.md) ||
      (this.dataset.desktopMenuTrigger === 'click' && theme.mediaMatches.md)
    ) {
      if (navLink && !hasHiddenToggle) {
        const siblingToggle = navLink.parentElement?.querySelector('.js-toggle');
        if (siblingToggle) {
          evt.preventDefault();
          el = siblingToggle;
        }
      }
    }

    // Resolve to the toggle/back element regardless of which child element was clicked
    el = evt.target.closest('.js-toggle, .js-back') || el;

    if (!el.matches('.js-toggle,.js-back')) return;

    if (el.matches('.js-toggle')) {
      // Handle child-level nav dropdown toggle
      const childToggle = el.closest('.child-nav__item--toggle');
      if (childToggle) {
        evt.preventDefault();
        this.opener = el;
        if (!childToggle.classList.contains('is-open')) {
          this.open(childToggle, false);
        } else {
          this.close(childToggle, true);
        }
        return;
      }

      // Handle top-level nav dropdown toggle
      const navDropdown = el.closest('.main-nav__item--dropdown');
      if (navDropdown) {
        evt.preventDefault();
        this.opener = el;

        if (!navDropdown.classList.contains('is-open')) {
          this.childNavOpen = true;

          if (!theme.mediaMatches.md) {
            mainMenuContent.classList.add('main-menu__content--no-focus');
          }

          if (this.closeOpenDisclosures({ except: navDropdown }) === 0 && theme.mediaMatches.md) {
            this.toggleOverlay(!this.overlayOpen);
          }

          this.open(navDropdown);
        } else {
          this.close(navDropdown, true);
          this.childNavOpen = false;
          this.toggleOverlay(false);
        }
      }
    } else if (el.matches('.js-back')) {
      evt.preventDefault();
      // A back button inside a third-level panel steps back just one level:
      // close that .child-nav__item--toggle (sliding the third level out)
      // and leave the top-level panel open. The second-level back button has
      // no .child-nav__item--toggle ancestor, so it falls through to closing
      // the whole top-level dropdown and unlocking the outer scroll.
      const childToggle = el.closest('.child-nav__item--toggle');
      if (childToggle) {
        this.close(childToggle, true);
      } else {
        const navDropdown = el.closest('.main-nav__item--dropdown');
        if (navDropdown) {
          this.close(navDropdown, true);
        }
        this.childNavOpen = false;

        if (!theme.mediaMatches.md) {
          mainMenuContent.classList.remove('main-menu__content--no-focus');
        }
      }
    }
  }

  /**
   * Handles a 'click' event on the overlay and a 'keyup' event on the nav.
   * @param {object} evt - Event object.
   */
  handleClose(evt) {
    if (evt && evt.type === 'keyup' && evt.key !== 'Escape') return;

    if (theme.mediaMatches.md) {
      if (this.closeOpenDisclosures() > 0) {
        this.toggleOverlay(false);
        this.childNavOpen = false;
      }
    } else if (this.mainContent.classList.contains('is-open')) {
      this.closeMainMenu(true);
      this.toggleOverlay(false);
      this.childNavOpen = false;
    }
  }

  /**
   * Toggles visibility of the background overlay.
   * @param {boolean} show - Show the overlay.
   */
  toggleOverlay(show) {
    this.overlayOpen = show;
    this.overlay.classList.toggle('overlay--nav', show);
    this.overlay.classList.toggle('is-visible', show);

    if (show) {
      // Add event handler (so the bound event listener can be removed).
      this.closeHandler = this.closeHandler || this.handleClose.bind(this);

      // Add event listeners (for while the nav is open).
      this.overlay.addEventListener('click', this.closeHandler);
      this.nav.addEventListener('keyup', this.closeHandler);

      // Auto-close on overlay hover is the natural counterpart to hover-open:
      // moving the mouse off the menu signals intent to dismiss. In click
      // mode the menu is opened by an explicit click, so hover-out close
      // would feel like the menu vanished for no reason — only the explicit
      // gestures (click overlay, click chevron, Escape) should close it.
      if (theme.mediaMatches.md && this.dataset.desktopMenuTrigger !== 'click') {
        this.overlay.addEventListener('mouseenter', this.closeHandler);
      }
    } else {
      // Remove event listener added on nav opening.
      this.overlay.removeEventListener('click', this.closeHandler);
      this.nav.removeEventListener('keyup', this.closeHandler);

      // Symmetric remove. The dataset value is stable across the lifetime
      // of one MainMenu instance (a setting change re-renders the section),
      // so this matches whichever branch ran in the open path.
      if (theme.mediaMatches.md && this.dataset.desktopMenuTrigger !== 'click') {
        this.overlay.removeEventListener('mouseenter', this.closeHandler);
      }
    }
  }

  /**
   * Closes the main menu content if it is open (called from search icon click).
   */
  closeMainContent() {
    if (this.mainContent.classList.contains('is-open')) {
      this.closeMainMenu(true);
      this.toggleOverlay(false);
      this.childNavOpen = false;
    }
  }

  /**
   * Opens a nav dropdown element.
   *
   * Runs synchronously — `.is-open` (display:block, opacity:0) is applied,
   * a forced layout flush commits that intermediate state, then `.is-visible`
   * (opacity:1) is applied so the opacity transition fires from 0 → 1. The
   * prior implementation used a double-requestAnimationFrame to span those
   * two paints, which left the panel rendered at opacity 0 for ~32ms before
   * the fade-in even started — perceived as a flicker on every open and an
   * asymmetric crossfade on every swap (old menu fading out from t=0, new
   * only beginning at t=32ms). Sync reflow eliminates that gap.
   *
   * @param {Element} el - The dropdown element.
   * @param {boolean} [mainMenuOpen=true] - Whether to trap focus and lock scroll.
   */
  open(el, mainMenuOpen = true) {
    // If a pending close was scheduled for this element, cancel it — we want it open.
    this.clearCloseTimer(el);
    // Belt-and-braces for the click / keyboard / theme-editor paths that bypass
    // the hover handler. Idempotent — once data-src is consumed the query matches
    // nothing.
    this.hydrateImages(el);

    el.classList.add('is-open');
    el.querySelector('.main-nav__toggle-btn')?.setAttribute('aria-expanded', 'true');

    if (theme.mediaMatches.md && !el.classList.contains('js-mega-nav')) {
      // Standard (non-mega) dropdown: position fix + grandchild width cap.
      // getBoundingClientRect/clientWidth reads force a layout commit, which
      // doubles as the reflow the .is-visible transition needs to fire from
      // a stable display:block, opacity:0 starting state.
      const dropdownContainer = el.querySelector('.main-nav__child');
      if (dropdownContainer?.getBoundingClientRect().right > window.innerWidth) {
        dropdownContainer.classList.add('main-nav__child--offset-right');
      }
      const dropdown = el.querySelector('.child-nav--dropdown');
      if (dropdown) {
        dropdown.querySelectorAll('.main-nav__grandchild').forEach((grandchildElem) => {
          grandchildElem.style.maxWidth = `${dropdown.clientWidth}px`;
        });
      }
    } else if (theme.mediaMatches.md) {
      // Mega-nav has no measurements but still needs the forced reflow so the
      // .is-visible toggle below produces an opacity transition instead of
      // collapsing into the same paint as the display:block toggle above.
      // Reading offsetHeight is the standard one-liner for that.
      void el.offsetHeight;
    }

    // Child-level panel height animation (standard non-mega dropdown on
    // desktop only). On mobile every third-level panel — mega or not —
    // slides in from the right like the second level (CSS-driven off
    // .is-open), so it must NOT get the inline height animation here.
    // Mega nav children on desktop fade via .is-visible (handled below).
    const panel = el.querySelector(':scope > .child-nav__panel');
    const needsHeight = !!panel && theme.mediaMatches.md && !el.closest('.mega-nav');

    if (needsHeight) {
      panel.style.height = '0';
      requestAnimationFrame(() => {
        panel.style.height = `${panel.scrollHeight}px`;
        panel.addEventListener(
          'transitionend',
          () => {
            panel.style.height = '';
          },
          { once: true },
        );
      });
    } else {
      const sidebarContainer = el.querySelector('.child-nav');
      const firstSidebarPanel = sidebarContainer?.querySelector('.js-sidebar-hover.is-visible .child-nav__panel');
      if (firstSidebarPanel) {
        sidebarContainer.style.setProperty('--sidebar-height', `${firstSidebarPanel.scrollHeight}px`);
      }
      el.classList.add('is-visible');
    }

    if (mainMenuOpen) {
      removeTrapFocus();
      trapFocus(el);

      // Desktop megamenu intentionally does NOT lock body scroll: the
      // `overflow-hidden` class removes the scrollbar and the paired
      // `body.overflow-hidden::after` pseudo paints a gutter strip,
      // producing a visible flash on open/close that reads as flicker.
      // Mobile drawer keeps its own lock via openMainMenu/closeMainMenu.
    }
  }

  /**
   * Closes a nav dropdown element.
   *
   * Desktop top-level path uses a deterministic setTimeout to strip `.is-open`
   * once the fade has elapsed, rather than listening for `transitionend`.
   * `transitionend` is silently dropped by browsers when the transition is
   * cancelled mid-flight (e.g. a re-open swap that re-adds `.is-visible`,
   * an opacity value that didn't actually change, throttling on a hidden
   * tab). When that happens, `.is-open` would stick — the disclosure's row
   * underline stays lit and openMenuFromMouseEnter's `is-open` guard would
   * reject every subsequent hover, locking the menu shut.
   *
   * @param {Element} el - The dropdown element.
   * @param {boolean} [transition=true] - Whether to run the exit transition.
   */
  close(el, transition = true) {
    el.querySelector('.main-nav__toggle-btn')?.setAttribute('aria-expanded', 'false');
    // A re-arming close starts from a clean slate — no stale timer for this element.
    this.clearCloseTimer(el);

    const panel = el.querySelector(':scope > .child-nav__panel');
    // Mirror open(): only the desktop standard dropdown animates height back
    // to zero. Mobile third-level panels slide out (CSS) when .is-open is
    // stripped in the mobile branch below.
    const needsHeight = !!panel && theme.mediaMatches.md && !el.closest('.mega-nav');

    if (transition && needsHeight) {
      // Animate height back to zero, then remove is-open once the transition ends.
      panel.style.height = `${panel.scrollHeight}px`;
      requestAnimationFrame(() => {
        panel.style.height = '0';
      });
      panel.addEventListener(
        'transitionend',
        () => {
          panel.style.height = '';
          el.classList.remove('is-open');
          removeTrapFocus(this.opener);
          this.opener = null;
          if (!theme.mediaMatches.md && this.mainContent.classList.contains('is-open')) {
            const openDropdown = this.nav.querySelector('.main-nav__item--dropdown.is-open');
            trapFocus(openDropdown || this);
          }
        },
        { once: true },
      );
    } else if (transition && theme.mediaMatches.md) {
      // Desktop top-level. Defensive: open() is synchronous so is-visible
      // should always be present alongside is-open here, but if an external
      // code path (Shopify section reload, theme-editor sync) added is-open
      // directly there's no fade to wait for — strip is-open immediately.
      if (!el.classList.contains('is-visible')) {
        el.classList.remove('is-open');
        removeTrapFocus(this.opener);
        this.opener = null;
      } else {
        el.classList.remove('is-visible');
        // +50ms safety margin so the timer fires after the visual fade completes,
        // not on top of the final paint.
        const tid = setTimeout(() => {
          this.closeTimers.delete(el);
          // Re-check: a re-hover may have re-added is-visible to reverse the close.
          if (el.classList.contains('is-open') && !el.classList.contains('is-visible')) {
            el.classList.remove('is-open');
            removeTrapFocus(this.opener);
            this.opener = null;
          }
        }, this.fadeMs + 50);
        this.closeTimers.set(el, tid);
      }
    } else {
      // Mobile top-level: removing is-open triggers the CSS slide-out.
      // No-transition close: remove both immediately.
      el.classList.remove('is-open', 'is-visible');
      removeTrapFocus(this.opener);
      this.opener = null;
      if (!theme.mediaMatches.md && this.mainContent.classList.contains('is-open')) {
        trapFocus(this);
      }
    }

    setTimeout(() => {
      const offsetMenu = el.querySelector('.main-nav__child--offset-right');
      if (offsetMenu) offsetMenu.classList.remove('main-nav__child--offset-right');
    }, 200);

    // Only remove body scroll lock for top-level items (child-level closures
    // should not affect the lock set by the open top-level dropdown).
    if (theme.mediaMatches.md && !el.closest('.main-nav__child')) {
      document.body.classList.remove('overflow-hidden', 'fixed');
    }
  }

  /**
   * Cancels a pending close finalisation timer for an element, if any.
   * Used to keep open() and a mid-close re-hover idempotent: re-opening
   * a disclosure that was about to strip its `.is-open` should leave it
   * marked open, not let the prior timer wipe the class out from under
   * the new open state.
   * @param {Element} el - The dropdown element.
   */
  clearCloseTimer(el) {
    const tid = this.closeTimers.get(el);
    if (tid) {
      clearTimeout(tid);
      this.closeTimers.delete(el);
    }
  }

  /**
   * Promotes any `data-src` / `data-srcset` / `data-sizes` attributes inside
   * the given subtree into real `src` / `srcset` / `sizes` attributes so the
   * browser starts fetching. Called on hover intent (mouseenter on a nav row)
   * to overlap the image fetch with the menu's open animation, and again from
   * open() to cover the click / keyboard / theme-editor paths.
   *
   * Idempotent — once the data-* attributes have been consumed the
   * `img[data-src]` query matches nothing, so repeat calls are free.
   *
   * Why this exists: the mega-menu can ship dozens of collection thumbnails
   * per page. With native `src=` the URLs are emitted at parse time and
   * aggressive browsers / preloaders may fetch them on every page nav even
   * inside `display:none` ancestors, bloating navigation. Deferring to JS
   * gives a hard guarantee that no megamenu image is requested until the
   * user is actually about to look at it.
   *
   * @param {Element} rootEl - Subtree to hydrate.
   */
  hydrateImages(rootEl) {
    rootEl.querySelectorAll('img[data-src]').forEach((img) => {
      if (img.dataset.srcset) {
        img.srcset = img.dataset.srcset;
        img.removeAttribute('data-srcset');
      }
      if (img.dataset.sizes) {
        img.sizes = img.dataset.sizes;
        img.removeAttribute('data-sizes');
      }
      // Promote from lazy to eager BEFORE setting src. The mega-menu panels
      // live inside a display:none ancestor; a lazy image inside display:none
      // never enters the viewport, so the fetch would never start. Eager is
      // viewport-agnostic — it kicks off the request the moment src is set,
      // which is exactly what we want on hover intent.
      img.removeAttribute('loading');
      img.src = img.dataset.src;
      img.removeAttribute('data-src');
    });
  }

  /**
   * Closes every top-level disclosure currently flagged `.is-open`, optionally
   * skipping one (used when swapping into a new menu).
   *
   * Why iterate instead of querying a single match: `close()` removes
   * `.is-visible` synchronously but leaves `.is-open` set until the panel's
   * `transitionend` fires (~150–300ms later). During fast hover/click swaps
   * more than one disclosure can sit in that mid-close window at the same
   * time. A single `querySelector` only returns the first DOM match, so any
   * additional lingering panel would silently remain rendered — that's the
   * "menus overlap and never close" symptom.
   *
   * @param {object} [opts]
   * @param {Element} [opts.except] - Disclosure to leave untouched.
   * @param {boolean} [opts.transition=true] - Animate the close, or strip state immediately.
   * @returns {number} Count of disclosures that were closed.
   */
  closeOpenDisclosures({ except = null, transition = true } = {}) {
    let count = 0;
    this.nav.querySelectorAll('.main-nav__item--dropdown.is-open').forEach((el) => {
      if (el !== except) {
        this.close(el, transition);
        count += 1;
      }
    });
    return count;
  }

  /**
   * Observes .main-menu__content size and hides the right-side
   * .secondary-nav-container (quicklinks + CTA) when the main nav would
   * otherwise wrap to a second row.
   *
   * No-ops when the section was rendered without a secondary nav or when
   * ResizeObserver is unavailable. The custom element's disconnectedCallback
   * disconnects the observer, so re-renders by the theme editor don't leak.
   */
  setupQuicklinksOverflowGuard() {
    this.secondaryNavContainer = this.querySelector('.secondary-nav-container');
    if (!this.secondaryNavContainer || !this.nav || !this.mainContent) return;
    if (typeof ResizeObserver === 'undefined') return;

    this.quicklinksOverflowObserver = new ResizeObserver(this.updateQuicklinksOverflow.bind(this));
    this.quicklinksOverflowObserver.observe(this.mainContent);
    // ResizeObserver fires once on .observe(), but call once explicitly so
    // initial state is correct even in environments that delay the first tick.
    this.updateQuicklinksOverflow();

    // Custom font loads shift glyph widths without resizing the observed box,
    // so RO never sees the change. If the initial measurement happened with
    // a fallback font (typical on first paint), re-measure once fonts settle.
    if (document.fonts && typeof document.fonts.ready?.then === 'function') {
      document.fonts.ready.then(() => this.updateQuicklinksOverflow());
    }
  }

  /**
   * Toggles the `main-menu__content--quicklinks-hidden` class based on
   * whether the main nav is wrapping. The detection is done by an optimistic
   * "show then measure" pass: remove the hide class, read getBoundingClientRect,
   * and re-add the class if items still wrap. Because both DOM mutations and
   * the rect read happen synchronously in the same task, the browser paints
   * the final state only — no visible flash from the optimistic show.
   *
   * Gated to the md breakpoint (>= 769px). Below that, .main-menu__content
   * stacks vertically and the two navs don't compete for horizontal space,
   * so the class is irrelevant; we still clear it defensively in case the
   * user resizes down from a wrapped desktop state.
   */
  updateQuicklinksOverflow() {
    if (!this.secondaryNavContainer || !this.nav || !this.mainContent) return;
    // Guard against a deferred callback (e.g. document.fonts.ready) firing
    // on an instance the theme editor already swapped out.
    if (!this.isConnected) return;

    if (!theme.mediaMatches.md) {
      this.mainContent.classList.remove('main-menu__content--quicklinks-hidden');
      return;
    }

    this.mainContent.classList.remove('main-menu__content--quicklinks-hidden');
    if (this.isMainNavWrapped()) {
      this.mainContent.classList.add('main-menu__content--quicklinks-hidden');
    }
  }

  /**
   * Detects whether `.main-nav` items have wrapped to more than one row by
   * comparing the top offsets of the first and last <li>. A 1px tolerance
   * absorbs sub-pixel rounding from fractional layout.
   *
   * NOTE: The inline pre-paint detector in sections/header.liquid duplicates
   * this logic so the class can be applied before main.js (deferred) loads.
   * Keep the two in sync.
   * @returns {boolean}
   */
  isMainNavWrapped() {
    const items = this.nav.children;
    if (items.length < 2) return false;
    const firstTop = items[0].getBoundingClientRect().top;
    const lastTop = items[items.length - 1].getBoundingClientRect().top;
    return lastTop - firstTop > 1;
  }

  /**
   * Decide whether to show a particular mega menu
   * @param {object} evt - Event object
   */
  handleBlockSelect(evt) {
    this.closeOpenDisclosures({ transition: false });

    if (evt.target.matches('.js-mega-nav')) {
      this.open(evt.target, false);
      this.toggleOverlay(true);
    }
  }

  /**
   * Decide whether to hide a particular mega menu
   * @param {object} evt - Event object
   */
  handleBlockDeselect(evt) {
    if (evt.target.matches('.js-mega-nav')) {
      this.close(evt.target, false);
      this.toggleOverlay(false);
    }
  }
}

customElements.define('main-menu', MainMenu);

class CarouselSlider extends theme.BaseElement {
  constructor() {
    super();
    window.initLazyScript(this, this.init.bind(this));
  }

  disconnectedCallback() {
    // BaseElement's AbortController covers this.on(); the scrollbar library is
    // not a listener, so it has to be torn down by hand or a Theme Editor
    // section reorder leaves its instance bound to a detached node (ADR-0001).
    super.disconnectedCallback();
    this.destroyOverlayScrollbars();
  }

  init() {
    // Ignore nested slides. The lookup is guarded because the element is a
    // public tag: every section that ships one renders at least one slide, but
    // that is 13 independent templates keeping an invariant nothing enforces,
    // and an app block could render none at all.
    const firstSlide = this.querySelector('.slider__item');
    if (!firstSlide) return;

    this.slides = Array.from(firstSlide.parentElement.children).filter((child) => !child.hasAttribute('hidden'));
    if (this.slides.length < 2) return;
    this.slider = this.querySelector('.slider');
    this.grid = this.querySelector('.slider__grid');
    this.nav = this.querySelector(`.slider-nav__btn[aria-controls='${this.slider.id}']`)?.closest('.slider-nav');
    this.rtl = document.dir === 'rtl';

    if (this.nav) {
      this.prevBtn = this.nav.querySelector('button[name="prev"]');
      this.nextBtn = this.nav.querySelector('button[name="next"]');
    }

    this.initSlider();

    // Any width change re-decides everything, not just a breakpoint crossing:
    // the track is sized in fractions of the container, so 1400px -> 1300px
    // changes what fits without crossing anything.
    this.on(window, 'on:debounced-resize', this.handleResize.bind(this));
  }

  /**
   * Whether the merchant has switched the carousel off at the current breakpoint.
   * @returns {boolean}
   */
  isDisabledAtBreakpoint() {
    const wide = theme.mediaMatches.sm;
    return Boolean((this.getAttribute('disable-mobile') && !wide) || (this.getAttribute('disable-desktop') && wide));
  }

  /**
   * Whether the track overflows its scrollport, i.e. whether this is a carousel
   * at all right now.
   *
   * This asks the browser rather than deriving it from `slidesPerPage`, because
   * the two can disagree: `slidesPerPage` is a rounded estimate built from
   * integer `clientWidth` reads, so a track that fits could still be reported as
   * one card short (arrows and a scrollbar on a carousel that does not scroll)
   * and vice versa. Overflow is the actual question, and `scrollWidth` answers it
   * exactly — including the cases the estimate cannot see, such as the trailing
   * gutter the last slide carries below 1024px, which lands inside the inner
   * container's padding and is therefore NOT overflow.
   *
   * The 1px tolerance absorbs sub-pixel layout rounding: fractional track widths
   * (a 5-across grid is `(100% - 4 × gap) / 5`) can leave a fraction of a pixel
   * of overflow that no user can see or reach. Any genuine "one more card" is
   * two orders of magnitude larger, so nothing real is swallowed.
   *
   * Sound in both states: the `[fits]` state clips with `overflow: hidden`, which
   * keeps the element a scroll container, so the measurement stays honest there
   * too and a carousel can always be measured back into scrolling.
   * @returns {boolean}
   */
  hasOverflow() {
    return this.slider.scrollWidth - this.slider.clientWidth > 1;
  }

  initSlider() {
    if (this.isDisabledAtBreakpoint()) {
      this.setCarouselState(false, false);
      return;
    }

    this.gridWidth = this.grid.clientWidth;

    // Distance between leading edges of adjacent slides (i.e. width of card + gap).
    this.slideSpan = this.getWindowOffset(this.slides[1]) - this.getWindowOffset(this.slides[0]);

    // Width of gap between slides.
    this.slideGap = this.slideSpan - this.slides[0].clientWidth;

    this.slidesPerPage = Math.max(1, Math.round((this.gridWidth + this.slideGap) / this.slideSpan));
    this.slidesToScroll = theme.settings.sliderItemsPerNav === 'page' ? this.slidesPerPage : 1;
    this.totalPages = this.slides.length - this.slidesPerPage + 1;

    const scrolls = this.hasOverflow();
    if (this.setCarouselState(scrolls)) return;
    if (!scrolls) return;

    this.sliderStart = this.getWindowOffset(this.slider);
    if (!this.sliderStart) this.sliderStart = (this.slider.clientWidth - this.gridWidth) / 2;
    this.sliderEnd = this.sliderStart + this.gridWidth;

    // Read once: the grab cursor and the handlers that make it true are the same
    // decision, and a second matchMedia call is a second chance to disagree.
    this.canDrag = window.matchMedia('(pointer: fine)').matches;
    if (this.canDrag) this.slider.classList.add('is-grabbable');

    this.addListeners();
    this.setButtonStates();

    // Init the custom scrollbars (lazy-loaded — both assets/overlay-scrollbars.min.js
    // AND assets/overlay-scrollbars.css are fetched on demand and cached for
    // subsequent slider instances; main.css contains only a pointer comment).
    // Only a scrolling carousel gets them: an inactive one has nothing to scroll,
    // and the library reserves a scrollbar strip that CSS cannot take back.
    if (!this.slider.classList.contains('slider--no-scrollbar')) {
      this.initOverlayScrollbars();
    }
  }

  /**
   * Hands the slider's scrollbar to OverlayScrollbars. Only ever called while
   * the track scrolls; paired with destroyOverlayScrollbars().
   */
  initOverlayScrollbars() {
    if (this.scrollbars || this.pendingScrollbars) return;

    // The library writes its layout arrangement (negative margins, an inset
    // offset and `width: calc(100% + <host padding>)`) as inline styles on the
    // viewport, and destroy() does not take them off an element it did not
    // create — the slider is ours, handed over via `elements.viewport`. Left
    // behind, that width pushes the whole track a gutter out of line with the
    // section heading, once the `[data-overlayscrollbars-viewport]` rule that
    // neutralised it stops matching. Snapshot it before the library writes.
    this.sliderInlineStyle = this.slider.getAttribute('style');

    this.pendingScrollbars = attachOverlayScrollbars(this.slider).then((instance) => {
      this.pendingScrollbars = null;
      if (!instance) return;

      // Both halves load lazily, so this can land after a resize already took
      // the carousel out of its scrolling state, or after the Theme Editor
      // removed the section outright. Keeping the instance then would restore
      // the very strip deactivate() had just removed, or bind the library to a
      // detached node that nothing will ever tear down again.
      if (!this.isConnected || this.hasAttribute('inactive')) {
        instance.destroy();
        this.restoreSliderStyle();
        return;
      }

      this.scrollbars = instance;
    });
  }

  /**
   * Removes the custom scrollbars and every trace they left on the slider.
   * Idempotent, so deactivating twice or disconnecting after one is safe.
   */
  destroyOverlayScrollbars() {
    if (!this.scrollbars) return;

    this.scrollbars.destroy();
    this.scrollbars = null;
    this.restoreSliderStyle();
  }

  /**
   * Puts back the `style` attribute the slider carried before the library wrote
   * its layout arrangement into it. See the snapshot in initOverlayScrollbars().
   */
  restoreSliderStyle() {
    if (this.sliderInlineStyle) {
      this.slider.setAttribute('style', this.sliderInlineStyle);
    } else {
      this.slider.removeAttribute('style');
    }
    this.sliderInlineStyle = null;
  }

  /**
   * Binds the listeners that only make sense while the carousel scrolls.
   *
   * Every handler is memoised on first use, so re-running this binds the same
   * function references the DOM already holds: an identical (type, listener)
   * pair is a no-op, and removeListeners() can still take them all off. Binding
   * a fresh closure each time would make add and remove drift apart — removal
   * only ever reaches the newest copy, so the older ones keep hijacking pointer
   * events on a track that deactivate() has already stopped scrolling.
   */
  addListeners() {
    // keep raw: removed in removeListeners() mid-lifetime when the track stops
    // scrolling, which BaseElement's all-or-nothing AbortController cannot do.
    if (this.nav) {
      this.scrollHandler = this.scrollHandler || debounce(this.handleScroll.bind(this));
      this.navClickHandler = this.navClickHandler || this.handleNavClick.bind(this);

      this.slider.addEventListener('scroll', this.scrollHandler, { passive: true });
      this.nav.addEventListener('click', this.navClickHandler);
    }

    if (this.canDrag) {
      this.mousedownHandler = this.mousedownHandler || this.handleMousedown.bind(this);
      this.mouseupHandler = this.mouseupHandler || this.handleMouseup.bind(this);
      this.mousemoveHandler = this.mousemoveHandler || this.handleMousemove.bind(this);

      this.slider.addEventListener('mousedown', this.mousedownHandler);
      this.slider.addEventListener('mouseup', this.mouseupHandler);
      this.slider.addEventListener('mouseleave', this.mouseupHandler);
      this.slider.addEventListener('mousemove', this.mousemoveHandler);
    }
  }

  /**
   * Idempotent: safe before anything was bound, and safe twice.
   * removeEventListener ignores an undefined handler.
   */
  removeListeners() {
    if (this.nav) {
      this.slider.removeEventListener('scroll', this.scrollHandler);
      this.nav.removeEventListener('click', this.navClickHandler);
    }

    this.slider.removeEventListener('mousedown', this.mousedownHandler);
    this.slider.removeEventListener('mouseup', this.mouseupHandler);
    this.slider.removeEventListener('mouseleave', this.mouseupHandler);
    this.slider.removeEventListener('mousemove', this.mousemoveHandler);
  }

  /**
   * Handles 'scroll' events on the slider element.
   */
  handleScroll() {
    this.currentIndex = Math.round(this.slider.scrollLeft / this.slideSpan);
    this.setButtonStates();
  }

  /**
   * Handles 'mousedown' events on the slider element.
   * @param {object} evt - Event object.
   */
  handleMousedown(evt) {
    this.mousedown = true;
    this.startX = evt.pageX - this.sliderStart;
    this.scrollPos = this.slider.scrollLeft;
    this.slider.classList.add('is-grabbing');
  }

  /**
   * Handles 'mouseup' events on the slider element.
   */
  handleMouseup() {
    this.mousedown = false;
    this.slider.classList.remove('is-grabbing');
    this.slider.classList.remove('is-dragging');
  }

  /**
   * Handles 'mousemove' events on the slider element.
   * @param {object} evt - Event object.
   */
  handleMousemove(evt) {
    if (!this.mousedown) return;
    evt.preventDefault();

    const x = evt.pageX - this.sliderStart;
    this.slider.scrollLeft = this.scrollPos - (x - this.startX) * 2;
    this.slider.classList.add('is-dragging');
  }

  /**
   * Handles 'click' events on the nav buttons container.
   * @param {object} evt - Event object.
   */
  handleNavClick(evt) {
    if (!evt.target.matches('.slider-nav__btn')) return;

    if ((evt.target.name === 'next' && !this.rtl) || (evt.target.name === 'prev' && this.rtl)) {
      this.scrollPos = this.slider.scrollLeft + this.slidesToScroll * this.slideSpan;
    } else {
      this.scrollPos = this.slider.scrollLeft - this.slidesToScroll * this.slideSpan;
    }

    this.slider.scrollTo({ left: this.scrollPos, behavior: 'smooth' });
  }

  /**
   * Re-measures and re-applies the whole state after the slider changes width.
   */
  handleResize() {
    this.removeListeners();
    this.initSlider();
  }

  /**
   * Gets the offset of an element from the edge of the viewport (left for ltr, right for rtl).
   * @param {number} el - Element.
   * @returns {number}
   */
  getWindowOffset(el) {
    return this.rtl ? window.innerWidth - el.getBoundingClientRect().right : el.getBoundingClientRect().left;
  }

  /**
   * Gets the visible state of a slide.
   * @param {Element} el - Slide element.
   * @returns {boolean}
   */
  getSlideVisibility(el) {
    const slideStart = this.getWindowOffset(el);
    const slideEnd = Math.floor(slideStart + this.slides[0].clientWidth);
    return slideStart >= this.sliderStart && slideEnd <= this.sliderEnd;
  }

  /**
   * Sets the active state of the carousel.
   * @param {boolean} active - Set carousel as active.
   * @param {boolean} [measured] - Whether `active` came from measuring the track.
   *   False on the one path that does not measure: the merchant switched the
   *   carousel off at this breakpoint. That track is not a fitting carousel but a
   *   plain grid, which the CSS may legitimately let scroll, so it must not pick
   *   up the [fits] clip.
   * @returns {boolean} True when the slider was re-initialised at a new width.
   *   Every measurement the caller took is stale at that point and the nested
   *   run has already finished the job, so the caller must return immediately —
   *   otherwise it wires a second copy of every listener onto the same slider.
   */
  setCarouselState(active, measured = true) {
    if (!active) {
      this.setAttribute('inactive', '');
      // Distinct from `inactive`, which ships in the markup as the pre-hydration
      // default: this one says the track has been MEASURED and fits, and it is
      // what lets the CSS stop the slider being a scrollport at all.
      this.toggleAttribute('fits', measured);
      this.deactivate();
      return false;
    }

    this.removeAttribute('inactive');
    this.removeAttribute('fits');

    // If slider width changed when activated, reinitialise it.
    if (this.gridWidth !== this.grid.clientWidth) {
      this.handleResize();
      return true;
    }

    return false;
  }

  /**
   * Returns the slider to a plain, non-scrolling row.
   *
   * The `inactive` attribute alone is not enough: it drives CSS, and CSS cannot
   * undo what JS put in place. Left behind, OverlayScrollbars keeps an
   * `!important` scrollbar strip reserved under a carousel that no longer
   * scrolls, `is-grabbable` keeps the grab cursor, and the drag handlers keep
   * hijacking pointer events on a track with nowhere to go. That combination is
   * what surfaced as a dead scroll area whenever a resize brought every card
   * into view.
   */
  deactivate() {
    this.removeListeners();
    this.slider.classList.remove('is-grabbable', 'is-grabbing', 'is-dragging');

    this.destroyOverlayScrollbars();

    // A carousel that fit while scrolled would otherwise keep its offset with no
    // way left to scroll back.
    if (this.slider.scrollLeft !== 0) {
      this.slider.scrollTo({ left: 0, behavior: 'instant' });
    }
  }

  /**
   * Sets the disabled state of the nav buttons.
   */
  setButtonStates() {
    if (!this.prevBtn && !this.nextBtn) {
      return;
    }

    this.prevBtn.disabled = this.getSlideVisibility(this.slides[0]) && this.slider.scrollLeft === 0;
    this.nextBtn.disabled = this.getSlideVisibility(this.slides[this.slides.length - 1]);
  }
}

customElements.define('carousel-slider', CarouselSlider);

class ProductRecommendations extends theme.BaseElement {
  constructor() {
    super();
    window.initLazyScript(this, this.init.bind(this), 500);
  }

  async init() {
    const { productId } = this.dataset;
    if (!productId) return;

    try {
      const response = await fetch(`${this.dataset.url}&product_id=${productId}`);
      if (!response.ok) throw new Error(response.status);

      const tmpl = document.createElement('template');
      tmpl.innerHTML = await response.text();

      const el = tmpl.content.querySelector('product-recommendations');
      if (el && el.hasChildNodes()) {
        this.innerHTML = el.innerHTML;
      }
    } catch (error) {
      console.log(error);
    }
  }
}

customElements.define('product-recommendations', ProductRecommendations);

window.initLazyScript = initLazyScript;

document.addEventListener('DOMContentLoaded', () => {
  setTimeout(() => {
    document.body.classList.add('dom-loaded');
  }, 0);

  setTimeout(() => {
    document.body.classList.add('dom-loaded-plus-6');
  }, 6000);

  if (theme.settings.externalLinksNewTab) {
    document.addEventListener('click', (evt) => {
      const link = evt.target.tagName === 'A' ? evt.target : evt.target.closest('a');
      if (link && link.tagName === 'A' && link.href && window.location.hostname !== new URL(link.href).hostname) {
        const isLoginLink = link.href && theme.routes.accountLoginUrl && new URL(link.href).hostname === new URL(theme.routes.accountLoginUrl).hostname;
        if (!isLoginLink) link.target = '_blank';
      }
    });
  }

  // Ensure anchor scrolling is smooth (this shouldn't be added in the CSS)
  document.addEventListener('click', (evt) => {
    if (evt.target.tagName === 'A' && window.location.hostname === new URL(evt.target.href).hostname && evt.target.href.includes('#')) {
      document.getElementsByTagName('html')[0].style.scrollBehavior = 'smooth';
      setTimeout(() => {
        document.getElementsByTagName('html')[0].style.scrollBehavior = '';
      }, 1000);
    }
  });
});

// Emit an event to indicate the page was restored from bfcache, https://web.dev/articles/bfcache
window.addEventListener('pageshow', (evt) => {
  if (evt.persisted) {
    document.dispatchEvent(new CustomEvent('on:bfcache:load-restore'));
  } else {
    document.dispatchEvent(new CustomEvent('on:bfcache:load-normal'));
  }
});

// Init mobile-only scrolling where needed.
// OverlayScrollbars is lazy-loaded only if a `.slider--mobile` is actually present
// on the page; saves ~30 KB on pages that have no mobile scrollers (password, 404, etc.).
//
// These sliders are plain markup rather than custom elements (promo-grid,
// product-comparison-grid), so there is no element lifecycle to hang
// the instance off and nothing here tears one down — the Theme Editor replaces
// the whole section, taking the host and its instance with it. That is why the
// re-run below has to skip sliders already carrying the library's viewport
// attribute: without it, every section load would re-wrap every OTHER section's
// sliders as well.
{
  const initMobileScrollbars = () => {
    const sliders = [...document.querySelectorAll('.slider--mobile')].filter((slider) => !slider.hasAttribute('data-overlayscrollbars-viewport'));
    sliders.forEach((slider) => attachOverlayScrollbars(slider));
  };

  initMobileScrollbars();
  document.addEventListener('shopify:section:load', initMobileScrollbars);
}
